import { openSplitViewState } from '@approvalmax/ui/src/components';
import { hooks } from '@approvalmax/utils';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { selectors } from 'modules/common';
import { updateXeroAirwallexBPDetails } from 'pages/requestForm/actions';
import { getAirwallexContext } from 'pages/requestForm/selectors/airwallex/airwallexSelectors';
import { FC, memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { splitViewGoToBarDefaultSettings, splitViewGoToBarSettingsState } from 'shared/components';
import { useGetBatchPaymentsRequestAmounts } from 'shared/data/webApp/v2';
import { activeAttachmentIdState, ContentSplitViewMode, contentSplitViewState } from 'shared/states';

import { useXeroAirwallexBatchPaymentFormContext } from '../../XeroAirwallexBatchPaymentForm.context';
import { getColumnDefinitions } from './BillsInformation.config';
import { usePrepopulateXeroBankAccount } from './BillsInformation.hooks';
import { messages } from './BillsInformation.messages';
import { EmptyState, EmptyText, Root, StyledLineItemsTable, TableContainer } from './BillsInformation.styles';
import { BillsInformationProps, OnSelectAttachmentParams } from './BillsInformation.types';

const BillsInformation: FC<BillsInformationProps> = (props) => {
    const { request, setAddBeneficiaryPopupData } = props;

    const airwallexContext = useSelector(getAirwallexContext);
    const profile = useSelector(selectors.profile.getProfile);
    const is2faEnabled = profile.is2faEnabled;

    const dispatch = useDispatch();

    const {
        billBatchPayment,
        removeSelectableBillById,
        changeBillInvoiceItemField,
        showErrors,
        billInvoiceRequestIdsWithError,
        billInvoiceRequestIdsWithExceededAmount,
        changeBeneficiary,
        changeBankAccount,
        companyCurrency,
        canCreateBeneficiary,
        checkBeneficiaryResultsByInvoiceId,
        editMode,
        removeLinesDisabled,
    } = useXeroAirwallexBatchPaymentFormContext();

    const { items } = billBatchPayment;
    const prevItems = hooks.usePrevious(items);

    const setOpenSplitView = useSetAtom(openSplitViewState);
    const [contentSplitView, setContentSplitView] = useAtom(contentSplitViewState);
    const setSplitViewGoToBarSettings = useSetAtom(splitViewGoToBarSettingsState);
    const activeAttachmentId = useAtomValue(activeAttachmentIdState);

    usePrepopulateXeroBankAccount({
        lastUsedXeroBankAccounts: airwallexContext.lastUsedXeroBankAccounts,
        changeBankAccount,
        items,
        prevItems,
    });

    const { data: batchPaymentsRequestAmounts, isFetching: isFetchingGetBatchPaymentsRequestAmounts } =
        useGetBatchPaymentsRequestAmounts({
            path: {
                companyId: request.companyId,
                requestId: request.id,
            },
        });

    const onSelectAttachment = useCallback(
        ({ attachmentId, xeroBatchPaymentInvoiceId, xeroBatchPaymentInvoiceName }: OnSelectAttachmentParams) => {
            dispatch(updateXeroAirwallexBPDetails(billBatchPayment));

            setOpenSplitView(true);
            setContentSplitView({
                mode: ContentSplitViewMode.AttachmentsToBillLinkedToBatchPayment,
                attachmentId,
                billId: xeroBatchPaymentInvoiceId,
                billFriendlyName: xeroBatchPaymentInvoiceName,
            });
            setSplitViewGoToBarSettings({
                show: true,
                targetContent: {
                    mode: ContentSplitViewMode.BillLinkedToBatchPayment,
                    billId: xeroBatchPaymentInvoiceId,
                },
                targetContentName: xeroBatchPaymentInvoiceName,
            });
        },
        [dispatch, billBatchPayment, setOpenSplitView, setContentSplitView, setSplitViewGoToBarSettings]
    );

    const handleSelectBill = useCallback(
        (xeroBatchPaymentInvoiceId: string) => {
            setContentSplitView({
                mode: ContentSplitViewMode.BillLinkedToBatchPayment,
                billId: xeroBatchPaymentInvoiceId,
            });
            setOpenSplitView(true);
        },
        [setContentSplitView, setOpenSplitView]
    );

    const handleRemoveSelectableBillById = useCallback(
        (xeroBillInvoiceRequestId: string) => {
            removeSelectableBillById(xeroBillInvoiceRequestId);

            const isBillInSplitView =
                contentSplitView.mode === ContentSplitViewMode.BillLinkedToBatchPayment &&
                contentSplitView.billId === xeroBillInvoiceRequestId;
            const isAttachmentOfBillInSplitView =
                contentSplitView.mode === ContentSplitViewMode.AttachmentsToBillLinkedToBatchPayment &&
                contentSplitView.billId === xeroBillInvoiceRequestId;

            if (openSplitViewState && (isBillInSplitView || isAttachmentOfBillInSplitView)) {
                setOpenSplitView(false);
                setContentSplitView({ mode: ContentSplitViewMode.Empty });
                setSplitViewGoToBarSettings(splitViewGoToBarDefaultSettings);
            }
        },
        [contentSplitView, removeSelectableBillById, setContentSplitView, setSplitViewGoToBarSettings, setOpenSplitView]
    );

    const columnDefinitions = useMemo(
        () =>
            getColumnDefinitions({
                removeSelectableBillById: handleRemoveSelectableBillById,
                changeBillInvoiceItemField,
                showErrors,
                billInvoiceRequestIdsWithError,
                billInvoiceRequestIdsWithExceededAmount,
                onSelectAttachment,
                onSelectBill: handleSelectBill,
                activeAttachmentId,
                sourceCurrencies: airwallexContext.currencies.map((c) => ({ id: c, text: c })),
                paymentPurposes: airwallexContext.paymentPurposes,
                paymentFees: airwallexContext.paymentFees,
                changeBeneficiary,
                changeBankAccount,
                companyCurrency,
                setAddBeneficiaryPopupData,
                is2faEnabled,
                canCreateBeneficiary,
                checkBeneficiaryResultsByInvoiceId,
                editMode,
                removeLinesDisabled,
                amounts: batchPaymentsRequestAmounts?.items,
                isAmountsLoading: isFetchingGetBatchPaymentsRequestAmounts,
            }),
        [
            handleRemoveSelectableBillById,
            changeBillInvoiceItemField,
            showErrors,
            billInvoiceRequestIdsWithError,
            billInvoiceRequestIdsWithExceededAmount,
            onSelectAttachment,
            handleSelectBill,
            activeAttachmentId,
            airwallexContext.currencies,
            airwallexContext.paymentPurposes,
            airwallexContext.paymentFees,
            changeBeneficiary,
            changeBankAccount,
            companyCurrency,
            setAddBeneficiaryPopupData,
            is2faEnabled,
            canCreateBeneficiary,
            checkBeneficiaryResultsByInvoiceId,
            editMode,
            removeLinesDisabled,
            batchPaymentsRequestAmounts?.items,
            isFetchingGetBatchPaymentsRequestAmounts,
        ]
    );

    return (
        <Root>
            {items.length ? (
                <TableContainer>
                    <StyledLineItemsTable columnDefinitions={columnDefinitions} lineItems={items} />
                </TableContainer>
            ) : (
                <EmptyState>
                    <EmptyText>{messages.emptyText}</EmptyText>
                </EmptyState>
            )}
        </Root>
    );
};

export default memo(BillsInformation);
